import React, { FC } from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import {
  Button,
  ButtonPriority as PRIORITY,
  ButtonSize as SIZE,
  Spinner,
} from 'wix-ui-tpa/cssVars';
import { classes, st } from './BookButton.st.css';
import settingsParams from '../../../../../settingsParams';
import { DataHooks } from './consts';
import { ButtonStyleOptions } from '../../../../../../../types/types';
import { useWidgetViewModel } from '../../../../hooks/useWidgetViewModel';
import stylesParams from '../../../../../stylesParams';

export interface BookButtonProps {
  onClick: () => void;
  loading?: boolean;
  isNoBookFlow: boolean;
  isPendingApprovalFlow: boolean;
  showViewCourse?: boolean;
}

export const BookButton: FC<BookButtonProps> = ({
  onClick,
  isNoBookFlow,
  isPendingApprovalFlow,
  showViewCourse,
  loading,
}) => {
  const { isMobile } = useEnvironment();
  const { shouldWorkWithAppSettings } = useWidgetViewModel();
  const settings = useSettings();
  const styles = useStyles();
  const { t } = useTranslation();
  const theme = settings.get(settingsParams.bookButtonStyle);

  const isSecondary =
    shouldWorkWithAppSettings &&
    (theme === ButtonStyleOptions.SQUARE_HOLE ||
      theme === ButtonStyleOptions.CIRCLE_HOLE);

  const spinnerDiameter = isMobile
    ? styles.get(stylesParams.mobileBookButtonFontSize)
    : styles.get(stylesParams.bookButtonFont).size!;

  const getContent = () => {
    if (showViewCourse) {
      return (
        settings.get(settingsParams.viewCourseButtonText) ||
        t('info-card.design.button.view-course')
      );
    }
    if (isNoBookFlow) {
      return (
        settings.get(settingsParams.noBookFlowText) ||
        t('info-card.design.button.more-info')
      );
    }
    if (isPendingApprovalFlow) {
      return (
        settings.get(settingsParams.pendingApprovalText) ||
        t('info-card.design.button.pending-approval')
      );
    }
    return (
      settings.get(settingsParams.bookButtonText) ||
      t('info-card.design.button.book-it')
    );
  };

  return (
    <div
      data-hook={DataHooks.ROOT}
      className={st(classes.root, {
        theme,
        isMobile,
        shouldWorkWithAppSettings,
      })}
    >
      <Button
        data-hook={DataHooks.BUTTON}
        className={st(classes.button)}
        size={SIZE.medium}
        priority={isSecondary ? PRIORITY.secondary : PRIORITY.primary}
        onClick={onClick}
        fullWidth={isMobile}
        aria-label={getContent()}
        role="button"
        tabIndex={0}
        disabled={loading}
        upgrade
      >
        {loading ? (
          <div className={classes.spinnerWrapper}>
            <Spinner
              className={classes.spinner}
              diameter={spinnerDiameter}
              data-hook={DataHooks.SPINNER}
            />
          </div>
        ) : (
          getContent()
        )}
      </Button>
    </div>
  );
};
